import React from "react";
import { Box, Card, CardContent, Grid, Skeleton, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";

import { useUseGetAdminDashboardStatsQuery } from "../../../store/services/admin/admin.service";
import { IAdminDashboardStatsResponse } from "../../../store/services/affiliate/dto/affiliate.dto";
import { INVITATION_STATUS, INVITATION_STATUSES } from "../../../store/services/invitations/dto/invitations.dto";
import { InvitationStatusColor } from "../../../constants";

const STATUS_ORDER: INVITATION_STATUS[] = [
	INVITATION_STATUS.DRAFT,
	INVITATION_STATUS.PENDING,
	INVITATION_STATUS.PUBLISHED,
	INVITATION_STATUS.ACTION_REQUIRED,
	INVITATION_STATUS.REJECTED,
	INVITATION_STATUS.SUSPENDED,
	INVITATION_STATUS.DELETED
];

const statusDataKey: Record<INVITATION_STATUS, keyof IAdminDashboardStatsResponse["data"]> = {
	[INVITATION_STATUS.DRAFT]: "draft",
	[INVITATION_STATUS.PENDING]: "pending",
	[INVITATION_STATUS.PUBLISHED]: "published",
	[INVITATION_STATUS.ACTION_REQUIRED]: "action_required",
	[INVITATION_STATUS.REJECTED]: "rejected",
	[INVITATION_STATUS.SUSPENDED]: "suspended",
	[INVITATION_STATUS.DELETED]: "deleted"
};

const Overview = () => {
	const { data: stats, isLoading } = useUseGetAdminDashboardStatsQuery({});

	const total = stats?.data.total ?? 0;

	const chartData = STATUS_ORDER.map(s => ({
		id: s,
		value: stats?.data[statusDataKey[s]] ?? 0,
		label: INVITATION_STATUSES[s],
		color: InvitationStatusColor[s]
	}));

	return (
		<Box>
			<Typography variant={"h5"} mb={3}>
				Invitations Overview
			</Typography>

			<Grid container spacing={2} mb={3}>
				<Grid size={{ xs: 12, sm: 6, md: 3 }}>
					<Card>
						<CardContent>
							<Typography variant={"caption"} color={"text.secondary"}>
								Total
							</Typography>
							{isLoading ? <Skeleton width={80} height={48} /> : <Typography variant={"h4"}>{total}</Typography>}
						</CardContent>
					</Card>
				</Grid>
				{STATUS_ORDER.map(s => (
					<Grid size={{ xs: 12, sm: 6, md: 3 }} key={s}>
						<Card>
							<CardContent>
								<Box display={"flex"} alignItems={"center"} gap={1} mb={0.5}>
									<Box
										sx={{
											width: 10,
											height: 10,
											borderRadius: "50%",
											backgroundColor: InvitationStatusColor[s]
										}}
									/>
									<Typography variant={"caption"} color={"text.secondary"}>
										{INVITATION_STATUSES[s]}
									</Typography>
								</Box>
								{isLoading ? (
									<Skeleton width={60} height={48} />
								) : (
									<Typography variant={"h4"}>{stats?.data[statusDataKey[s]] ?? 0}</Typography>
								)}
							</CardContent>
						</Card>
					</Grid>
				))}
			</Grid>

			<Grid container spacing={2}>
				<Grid size={{ xs: 12, md: 12 }}>
					<Card>
						<CardContent>
							<Typography variant={"subtitle1"} mb={1}>
								Status distribution
							</Typography>
							{isLoading ? (
								<Skeleton variant={"rectangular"} width={"100%"} height={300} />
							) : (
								<PieChart
									slotProps={{
										legend: {
											direction: "horizontal"
										}
									}}
									series={[
										{
											data: chartData,
											innerRadius: 50,
											outerRadius: 110,
											paddingAngle: 2,
											cornerRadius: 4,
											highlightScope: { fade: "global", highlight: "item" }
										}
									]}
									height={300}
								/>
							)}
						</CardContent>
					</Card>
				</Grid>
				<Grid size={{ xs: 12, md: 12 }}>
					<Card>
						<CardContent>
							<Typography variant={"subtitle1"} mb={1}>
								Invitations per status
							</Typography>
							{isLoading ? (
								<Skeleton variant={"rectangular"} width={"100%"} height={300} />
							) : (
								<BarChart
									xAxis={[
										{
											scaleType: "band",
											data: chartData.map(d => d.label),
											colorMap: {
												type: "ordinal",
												values: chartData.map(d => d.label),
												colors: chartData.map(d => d.color)
											}
										}
									]}
									series={[{ data: chartData.map(d => d.value), label: "Invitations" }]}
									height={300}
								/>
							)}
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</Box>
	);
};

export default Overview;
